import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TimerProvider } from "./contexts/TimerContext";
import { ScreenCaptureProvider } from "./contexts/ScreenCaptureContext";
import { SubscriptionProvider, SubscriptionGuard } from "./components/SubscriptionGuard";
import { Toaster } from "./components/ui/sonner";
import IdleAlertDialog from "./components/IdleAlertDialog";
import LocationTracker from "./components/LocationTracker";

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SSOCallback from "./pages/SSOCallback";
import SSOComplete from "./pages/SSOComplete";
import GoogleCallback from "./pages/GoogleCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyCallback from "./pages/VerifyCallback";
import Dashboard from "./pages/Dashboard";
import TimeTracker from "./pages/TimeTracker";
import Projects from "./pages/Projects";
import CalendarView from "./pages/CalendarView";
import Timesheet from "./pages/Timesheet";
import TimeOff from "./pages/TimeOff";
import Approvals from "./pages/Approvals";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import BillingPage from "./pages/BillingPage";
import WorkspaceSettings from "./pages/WorkspaceSettings";
import PeopleManagement from "./pages/admin/PeopleManagement";
import AdminProjects from "./pages/admin/AdminProjects";
import ProjectDetail from "./pages/admin/ProjectDetail";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminTimeOff from "./pages/admin/TimeOffManagement";
import AdminReports from "./pages/admin/AdminReports";
import ClientsManagement from "./pages/admin/ClientsManagement";
import ClientDetail from "./pages/admin/ClientDetail";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AcceptInvite from "./pages/AcceptInvite";
import SetupAccount from "./pages/SetupAccount";
import AIAssistant from "./pages/AIAssistant";
import TeamTimesheet from "./pages/TeamTimesheet";
import InvoicePage from "./pages/InvoicePage";
import Invoices from "./pages/Invoices";
import Holidays from "./pages/Holidays";
import Upgrade from "./pages/Upgrade";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import UsageDashboard from "./pages/UsageDashboard";
import UsageCategoryAdmin from "./pages/UsageCategoryAdmin";
import TeamScreenshots from "./pages/TeamScreenshots";
import TeamLocations from "./pages/TeamLocations";
import ActivityMonitor from "./pages/admin/ActivityMonitor";
import Payroll from "./pages/admin/Payroll";
import TDSReports from "./pages/admin/TDSReports";
import Integrations from "./pages/admin/Integrations";
import MyPayslips from "./pages/MyPayslips";
import ContactSupport from "./pages/ContactSupport";

import ImpersonationBanner from "./components/ImpersonationBanner";

// Super Admin Pages
import { SuperAdminProvider } from "./contexts/SuperAdminContext";
import SALogin from "./pages/superadmin/SALogin";
import SADashboard from "./pages/superadmin/SADashboard";
import SAOrganizations from "./pages/superadmin/SAOrganizations";
import SAOrgDetail from "./pages/superadmin/SAOrgDetail";
import SAPromoCodes from "./pages/superadmin/SAPromoCodes";
import SAFeatureFlags from "./pages/superadmin/SAFeatureFlags";
import SASupportTickets from "./pages/superadmin/SASupportTickets";
import SAAnnouncements from "./pages/superadmin/SAAnnouncements";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#00A3E0] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to email verification if not verified (skip for impersonated sessions)
  const isImpersonating = !!localStorage.getItem('inspirotracker_impersonation');
  if (user && user.email_verified === false && !isImpersonating) {
    return <Navigate to="/verify-email" replace />;
  }

  return (
    <TimerProvider>
      <ScreenCaptureProvider>
        <SubscriptionProvider>
          <SubscriptionGuard>
            {children}
          </SubscriptionGuard>
        </SubscriptionProvider>
        <IdleAlertDialog />
        <LocationTracker />
      </ScreenCaptureProvider>
    </TimerProvider>
  );
};

// Smart redirect based on user role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#00A3E0] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Admin always goes to admin dashboard
  if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  // Manager goes to admin dashboard by default (team view)
  if (user?.role === 'manager') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  // Employee goes to personal dashboard
  return <Navigate to="/dashboard" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#00A3E0] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Role-based redirect for authenticated users trying to access public routes
    if (user?.role === 'admin' || user?.role === 'manager') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/home" element={<LandingPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invite"
        element={<AcceptInvite />}
      />
      <Route
        path="/setup-account"
        element={<SetupAccount />}
      />
      <Route
        path="/verify-email"
        element={<VerifyEmail />}
      />
      <Route
        path="/verify-callback"
        element={<VerifyCallback />}
      />
      <Route
        path="/sso-callback"
        element={<SSOCallback />}
      />
      <Route
        path="/sso-complete"
        element={<SSOComplete />}
      />
      <Route
        path="/auth/callback/google"
        element={<GoogleCallback />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tracker"
        element={
          <ProtectedRoute>
            <TimeTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheet"
        element={
          <ProtectedRoute>
            <Timesheet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off"
        element={
          <ProtectedRoute>
            <TimeOff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holidays"
        element={
          <ProtectedRoute>
            <Holidays />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <Upgrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-timesheet"
        element={
          <ProtectedRoute>
            <TeamTimesheet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute>
            <Approvals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <Team />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-payslips"
        element={
          <ProtectedRoute>
            <MyPayslips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/workspace-settings"
        element={
          <ProtectedRoute>
            <WorkspaceSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/people"
        element={
          <ProtectedRoute>
            <PeopleManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute>
            <AdminProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <ProtectedRoute>
            <AdminApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/time-off"
        element={
          <ProtectedRoute>
            <AdminTimeOff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute>
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payroll"
        element={
          <ProtectedRoute>
            <Payroll />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tds-reports"
        element={
          <ProtectedRoute>
            <TDSReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute>
            <ClientsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:clientId"
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute>
            <AIAssistant />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact-support"
        element={
          <ProtectedRoute>
            <ContactSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice/:transactionId"
        element={
          <ProtectedRoute>
            <InvoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage"
        element={
          <ProtectedRoute>
            <UsageDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usage-categories"
        element={
          <ProtectedRoute>
            <UsageCategoryAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-screenshots"
        element={
          <ProtectedRoute>
            <TeamScreenshots />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-locations"
        element={
          <ProtectedRoute>
            <TeamLocations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-monitor"
        element={
          <ProtectedRoute>
            <ActivityMonitor />
          </ProtectedRoute>
        }
      />



      {/* Default redirect based on role */}
      <Route path="/" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />

      {/* Super Admin Routes */}
      <Route path="/superadmin/login" element={<SALogin />} />
      <Route path="/superadmin/dashboard" element={<SADashboard />} />
      <Route path="/superadmin/organizations" element={<SAOrganizations />} />
      <Route path="/superadmin/organizations/:orgId" element={<SAOrgDetail />} />
      <Route path="/superadmin/promo-codes" element={<SAPromoCodes />} />
      <Route path="/superadmin/feature-flags" element={<SAFeatureFlags />} />
      <Route path="/superadmin/support" element={<SASupportTickets />} />
      <Route path="/superadmin/announcements" element={<SAAnnouncements />} />

      <Route path="*" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

import { FeatureProvider } from './contexts/FeatureContext';

function App() {
  const isImpersonating = !!localStorage.getItem('inspirotracker_impersonation');
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureProvider>
          <SuperAdminProvider>
            <ImpersonationBanner />
            {isImpersonating && <div className="h-8" />}
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </SuperAdminProvider>
        </FeatureProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
